import { useEffect, useState } from 'react'
import { orderCode } from '../lib/order-code'
import { createRingAudio } from '../lib/ring-audio'
import { REASON_LABEL, ringLabel, type RingPair } from '../lib/ring-i18n'
import { whoPlate, type RingScreen } from '../lib/ring-rules'
import { useRing, type RingState } from '../lib/useRing'
import { useLang } from '../lib/i18n'

// R503 (c): what the till shows for the ring, HOP's RingPanel (R503 (b)) in the till's own
// primitives and tokens: which orders ring and why (the person and the plate, reason and age),
// the "Turn on the ring" tap, "Heard it", and a face for each other state. R537: labels show one
// language at a time, under the till's switch (they were the pair, English then Arabic).

function Pair({ pair }: { pair: RingPair }) {
  const lang = useLang()
  return <span lang={lang}>{pair[lang]}</span>
}

export function formatAge(ageSeconds: number): RingPair {
  const s = Math.max(0, Math.floor(ageSeconds))
  return s < 60 ? ringLabel('ringAgeSeconds', { n: String(s) }) : ringLabel('ringAgeMinutes', { n: String(Math.floor(s / 60)) })
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}

const LINE = 'text-sm'
const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2'

export function RingView({ ring }: { ring: RingState & { heard: () => void } }) {
  if (ring.phase === 'idle') return null
  if (ring.phase === 'checking') {
    return (
      <p data-ring="checking" aria-live="polite" className={`text-fleet-500 ${LINE}`}>
        <Pair pair={ringLabel('ringChecking')} />
      </p>
    )
  }
  if (ring.phase === 'failed') {
    return (
      <p
        data-ring="failed"
        aria-live="assertive"
        className={`rounded-lg bg-ember-100 px-4 py-3 font-semibold text-ember-700 ${LINE}`}
      >
        <Pair pair={ringLabel('ringFailed')} />
      </p>
    )
  }
  if (!ring.on) {
    return (
      <p data-ring="off" className={`text-fleet-500 ${LINE}`}>
        <Pair pair={ringLabel('ringOff')} />
      </p>
    )
  }
  if (ring.rows.length === 0) {
    return (
      <p data-ring="none" className={`text-fleet-500 ${LINE}`}>
        <Pair pair={ringLabel('ringNone')} />
      </p>
    )
  }
  const paused = ring.pausedUntil !== null
  return (
    <section
      data-ring="ringing"
      data-sounding={ring.sounding ? 'true' : 'false'}
      aria-labelledby="ring-title"
      className="flex flex-col gap-3 rounded-xl border-2 border-ember-500 bg-ember-50 p-4 text-fleet-800"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="ring-title" className="text-lg font-semibold text-ember-700">
          <Pair pair={ringLabel('ringTitle')} />
        </h2>
        <button
          type="button"
          data-ring="heard"
          disabled={paused}
          aria-describedby={paused ? 'ring-paused' : undefined}
          onClick={ring.heard}
          className={`min-h-tap rounded-lg bg-navy px-5 text-base font-semibold text-white hover:bg-navy/90 disabled:bg-fleet-300 ${FOCUS}`}
        >
          <Pair pair={ringLabel('ringHeard')} />
        </button>
      </div>
      {paused ? (
        <p id="ring-paused" data-ring="paused" className={`text-fleet-600 ${LINE}`}>
          <Pair pair={ringLabel('ringPaused', { time: clock(ring.pausedUntil!) })} />
        </p>
      ) : null}
      {ring.quiet ? (
        <p data-ring="quiet" className={`text-fleet-600 ${LINE}`}>
          <Pair pair={ringLabel('ringQuiet')} />
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-ember-100">
        {ring.rows.map((row) => {
          const en = whoPlate(row, 'en')
          const ar = whoPlate(row, 'ar')
          return (
            <li key={row.id} data-ring-order={row.id} className="flex flex-col gap-1 py-2 text-base">
              {en !== null && ar !== null ? (
                // Rook #67688: the person and the plate lead; the code follows, smaller, for lookups.
                <span className="flex flex-wrap items-baseline gap-x-3">
                  <span data-ring="who" className="font-bold">
                    <Pair pair={ringLabel('ringWhoPlate', { name: en.name, plate: { en: en.plate, ar: ar.plate } })} />
                  </span>
                  <span dir="ltr" data-ring="code" className="text-sm text-fleet-500">
                    {orderCode(row)}
                  </span>
                </span>
              ) : (
                <span dir="ltr" data-ring="code" className="font-bold">
                  {orderCode(row)}
                </span>
              )}
              {row.reasons.map((r) => (
                <span key={r.reason} data-ring-reason={r.reason} className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span>
                    <Pair pair={ringLabel(REASON_LABEL[r.reason])} />
                  </span>
                  <span data-ring="age" className="tabular-nums text-fleet-500">
                    <Pair pair={formatAge(r.ageSeconds)} />
                  </span>
                </span>
              ))}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

// The till's ring: the poll loop runs from mount and lists who waits; a tap turns the sound on
// (the browser lets a page make sound only after a tap).
// R572 (b1): `onRows` hands the ringing order ids, in the ring's order, to the till's Orders
// face, which lists those orders first; the ring keeps its one poll.
export function RingPanel({ screen = 'till', onRows }: { screen?: RingScreen; onRows?: (ids: string[]) => void }) {
  const [audio, setAudio] = useState<AudioContext | null>(null)
  const [arming, setArming] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const ring = useRing(screen, audio)
  useEffect(() => {
    onRows?.(ring.rows.map((r) => r.id))
  }, [ring.rows, onRows])

  async function arm() {
    setArming(true)
    const ctx = createRingAudio()
    if (ctx) {
      try {
        await ctx.resume()
      } catch {
        // fall through: the state check below decides
      }
    }
    setArming(false)
    if (ctx && ctx.state === 'running') {
      setBlocked(false)
      setAudio(ctx)
    } else {
      setBlocked(true)
    }
  }

  return (
    <div data-ring-panel={screen} className="w-full max-w-sm mx-auto flex flex-col gap-2 px-6 pt-4 font-body">
      {audio === null ? (
        // Rook #67784 (2): the list below shows before the tap; only the sound waits for it.
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-ring="arm"
            disabled={arming}
            aria-busy={arming}
            onClick={() => void arm()}
            className={`min-h-tap rounded-lg border-2 border-navy px-4 text-base font-semibold text-navy hover:bg-navy hover:text-white disabled:border-fleet-300 disabled:text-fleet-300 ${FOCUS}`}
          >
            <Pair pair={ringLabel(arming ? 'ringArming' : 'ringArm')} />
          </button>
          <span className="text-sm text-fleet-500">
            <Pair pair={ringLabel('ringArmHint')} />
          </span>
          {blocked ? (
            <p data-ring="blocked" aria-live="assertive" className="w-full text-sm font-semibold text-ember-700">
              <Pair pair={ringLabel('ringBlocked')} />
            </p>
          ) : null}
        </div>
      ) : null}
      <RingView ring={ring} />
    </div>
  )
}
